import React from 'react';
import * as PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import * as addHeightData from '../../../../redux/profiles/patientHealthboard/add-height-data';
import * as addWeightData from '../../../../redux/profiles/patientHealthboard/add-weight-data';
import {
  selectAddHeightDataPending,
  selectAddWeightDataPending,
} from '../../../../redux/profiles/patientHealthboard/selectors';
import { useFormState } from '../../../../hocs/useFormWithValidation';
import { COLORS } from '../../../../constants/styles';
import { IDS } from '../../../../constants/ids';
import DatePickerCustom from '../../../common/DatePickerCustom';
import TimePickerInput from '../../../common/TimePickerInput';
import Loader from '../../../common/Loader';
import TextInput from '../../../TextInput';
import Button from '../../../Button';
import Icon from '../../../Icon';
import {
  convertValueToMeters,
  convertValueWeightToKg,
  getCurrentTime,
  validateFutureTime,
} from '../../utils';
import styles from '../index.styl';

const {
  PATIENT_PORTAL: { ADD_BODY_INFO_FORM },
} = IDS;

export const AddBodyInfoForm = ({
  formatMessage,
  modalMode,
  isHeightInMeters,
  isWeightInKg,
}) => {
  const currentTime = moment().format('h:mm A');
  const { formState, handleFormChange } = useFormState({
    [ADD_BODY_INFO_FORM.TIME]: currentTime,
  });
  const currentDate = new Date();

  const dispatch = useDispatch();
  const pendingHeight = useSelector(selectAddHeightDataPending);
  const pendingWeight = useSelector(selectAddWeightDataPending);
  const combinedPending = pendingHeight || pendingWeight;
  const combinedDisable =
    !formState[ADD_BODY_INFO_FORM.FEET] ||
    !formState[ADD_BODY_INFO_FORM.INCH] ||
    !formState[ADD_BODY_INFO_FORM.WEIGHT] ||
    !formState[ADD_BODY_INFO_FORM.TIME] ||
    combinedPending;

  const handleSubmit = e => {
    e.preventDefault();
    const safeDate = moment(formState[ADD_BODY_INFO_FORM.DATE]).format(
      'YYYY-MM-D',
    );
    const safeTime = formState[ADD_BODY_INFO_FORM.TIME]
      ? `T${formState[ADD_BODY_INFO_FORM.TIME]}`
      : `T${moment().format('hh:mm A')}`;

    const heightValue = isHeightInMeters
      ? parseInt(formState[ADD_BODY_INFO_FORM.FEET], 10) / 100
      : convertValueToMeters(
          formState[ADD_BODY_INFO_FORM.FEET],
          formState[ADD_BODY_INFO_FORM.INCH],
        );
    const weightValue = isWeightInKg
      ? parseInt(formState[ADD_BODY_INFO_FORM.WEIGHT], 10)
      : convertValueWeightToKg(formState[ADD_BODY_INFO_FORM.WEIGHT]);

    dispatch(
      addHeightData.actions.send({
        local_height_measurement: {
          measured_at: moment(
            safeDate + safeTime,
            'YYYY-MM-DThh:mm A',
          ).format(),
          value: { baseValue: heightValue },
        },
      }),
    );

    dispatch(
      addWeightData.actions.send({
        local_weight_measurement: {
          measured_at: moment(
            safeDate + safeTime,
            'YYYY-MM-DThh:mm A',
          ).format(),
          value: { baseValue: weightValue },
        },
      }),
    );

    handleFormChange({
      [ADD_BODY_INFO_FORM.FEET]: '',
      [ADD_BODY_INFO_FORM.INCH]: '',
      [ADD_BODY_INFO_FORM.WEIGHT]: '',
    });
  };

  return (
    <div className={cn(styles.root, { [styles.rootModalMode]: modalMode })}>
      {!modalMode && (
        <div className={styles.title}>
          {formatMessage(
            { id: 'patient.portal.emptyState.addData' },
            {
              type: formatMessage({ id: 'patient.portal.emptyState.bodyInfo' }),
            },
          )}
        </div>
      )}
      {combinedPending && (
        <Loader className={cn(styles.loader, styles.loaderHeight)} />
      )}
      <form
        className={cn(styles.content, {
          [styles.modalMode]: modalMode,
        })}
        onSubmit={handleSubmit}
      >
        <div className={styles.dateWrapper}>
          <div className={styles.datePickerWrapper}>
            <DatePickerCustom
              value={formState[ADD_BODY_INFO_FORM.DATE] || currentDate}
              label={formatMessage({ id: 'common.date' })}
              name={ADD_BODY_INFO_FORM.DATE}
              maxDate={new Date()}
              formatDate="MMM D, YYYY"
              onChange={value =>
                handleFormChange({
                  [ADD_BODY_INFO_FORM.DATE]: value,
                  [ADD_BODY_INFO_FORM.TIME]: '',
                })
              }
              disabled={combinedPending}
            />
            <Icon
              name="calendar"
              style={{ fill: COLORS.bluePrimary }}
              className={styles.datePickerIcon}
            />
          </div>
          <TimePickerInput
            time={getCurrentTime()}
            value={formState[ADD_BODY_INFO_FORM.TIME]}
            name={ADD_BODY_INFO_FORM.TIME}
            onChange={value =>
              handleFormChange({
                [ADD_BODY_INFO_FORM.TIME]: value.toUpperCase(),
              })
            }
            label={formatMessage({ id: 'reports.time' })}
            placeholder={formatMessage({ id: 'reports.time' })}
            className={styles.timePickerWrapper}
            disabled={combinedPending}
            validateTime={pickTime =>
              validateFutureTime(
                formatMessage,
                pickTime,
                formState[ADD_BODY_INFO_FORM.DATE],
              )
            }
          />
        </div>
        <TextInput
          id={ADD_BODY_INFO_FORM.WEIGHT}
          type="number"
          label={formatMessage({ id: 'patient.healthboard.weight' })}
          limit={5}
          isFractionNumber
          placeholder={formatMessage({
            id: `profile.section.unitsSettings.weight.metric.${
              isWeightInKg ? 'kilograms' : 'pounds'
            }`,
          })}
          value={formState[ADD_BODY_INFO_FORM.WEIGHT]}
          onChange={value =>
            handleFormChange({ [ADD_BODY_INFO_FORM.WEIGHT]: value })
          }
          className={styles.weightInput}
          disabled={combinedPending}
          min={1}
        />
        <div className={styles.heightInputWrapper}>
          <TextInput
            id={ADD_BODY_INFO_FORM.FEET}
            type="number"
            label={formatMessage({ id: 'patient.healthboard.height' })}
            limit={isHeightInMeters ? 3 : 1}
            isFractionNumber
            placeholder={formatMessage({
              id: `profile.section.unitsSettings.height.metric.${
                isHeightInMeters ? 'meters' : 'feet_and_inches'
              }`,
            })}
            value={formState[ADD_BODY_INFO_FORM.FEET]}
            onChange={value =>
              handleFormChange({ [ADD_BODY_INFO_FORM.FEET]: value })
            }
            className={cn(styles.weightInput, styles.marginFirstChild)}
            disabled={combinedPending}
            min={1}
          />
          {!isHeightInMeters && (
            <TextInput
              id={ADD_BODY_INFO_FORM.INCH}
              type="number"
              limit={2}
              isFractionNumber
              placeholder={formatMessage({
                id: 'patient.healthboard.height.metric.inches',
              })}
              value={formState[ADD_BODY_INFO_FORM.INCH]}
              onChange={value =>
                handleFormChange({ [ADD_BODY_INFO_FORM.INCH]: value })
              }
              className={styles.weightInput}
              disabled={combinedPending}
            />
          )}
        </div>
        <Button
          id={ADD_BODY_INFO_FORM.ADD_BUTTON}
          raised
          color="primary"
          className={styles.addButton}
          disabled={combinedDisable}
        >
          {formatMessage({ id: 'common.add' })}
        </Button>
      </form>
    </div>
  );
};

AddBodyInfoForm.propTypes = {
  formatMessage: PropTypes.func,
  modalMode: PropTypes.bool,
  isHeightInMeters: PropTypes.bool,
  isWeightInKg: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/Forms/AddBodyInfoForm/index.jsx